import { cn } from "@/lib/utils";

const Bar = ({ className }: { className?: string }) => (
  <div className={cn("rounded bg-container-brand-minimal animate-pulse", className)} />
);

/** One city card on /places - @see `CityLinkCard`. */
const CityCardSkeleton = () => (
  <div className="flex flex-col items-center gap-2 p-3">
    <Bar className="w-full aspect-square rounded-xl" />
    <Bar className="h-4 w-3/4" />
    <Bar className="h-3 w-1/2" />
  </div>
);

/** One venue card on a city page - @see `VenueCard`. */
const VenueCardSkeleton = () => (
  <div className="flex flex-col overflow-hidden rounded-[8px] border border-border-token-brand-minimal">
    <Bar className="w-full aspect-[20/9] rounded-none" />
    <div className="flex flex-col gap-2 px-3 py-3">
      <Bar className="h-5 w-3/4" />
      <Bar className="h-4 w-1/2" />
      <Bar className="h-4 w-2/3" />
    </div>
  </div>
);

/**
 * /places as a skeleton, drawn to the page's own layout. Purely decorative: the caller
 * announces the wait once for a screen reader.
 */
export const PlacesIndexSkeleton = () => (
  <div aria-hidden="true" className="mx-auto flex max-w-[1160px] flex-col gap-8 px-4 py-6 md:py-10">
    <Bar className="h-9 w-72 max-w-full" />
    {[12, 6].map((cards, section) => (
      <div key={section} className="flex flex-col gap-3">
        <Bar className="h-6 w-40" />
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
          {Array.from({ length: cards }, (_, card) => (
            <CityCardSkeleton key={card} />
          ))}
        </div>
      </div>
    ))}
  </div>
);

/**
 * A city page as a skeleton, drawn to the page's own layout. Purely decorative: the caller
 * announces the wait once for a screen reader.
 */
export const PlacesCitySkeleton = () => (
  <div aria-hidden="true" className="mx-auto flex max-w-[1160px] flex-col gap-6 px-4 pt-2 pb-6 md:pt-4 md:pb-10">
    <div className="flex flex-col gap-1">
      <div className="flex min-h-[44px] items-center">
        <Bar className="h-5 w-40" />
      </div>
      <div className="flex flex-col gap-2">
        <Bar className="h-9 w-80 max-w-full" />
        <Bar className="h-4 w-24" />
      </div>
    </div>
    <div className="flex flex-wrap gap-2">
      {["w-36", "w-32", "w-36"].map((width, chip) => (
        <Bar key={chip} className={cn("h-11 rounded-full", width)} />
      ))}
    </div>
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 6 }, (_, card) => (
        <VenueCardSkeleton key={card} />
      ))}
    </div>
  </div>
);
